"use client";

import { useState } from "react";
import { cn } from "@/lib/utils";
import { trackEvent } from "@/lib/analytics";

type Answer = "y" | "n";

export interface TierCheckCopy {
  heading: string;
  intro: string;
  questions: readonly string[];
  yes: string;
  no: string;
  resultLabel: string;
  tiers: { lite: string; standard: string; regulated: string };
  why: { lite: string; standard: string; regulated: string };
  start: { lite: string; standard: string; regulated: string };
  extraConnectors: string;
  extraPolicy: string;
  note: string;
  rules: readonly string[];
  rulesLabel: string;
  cta: string;
}

/**
 * Two-minute tier check. Same logic as the AGK Pre-read v1.2 check:
 * Q1 yes -> Regulated; else any of Q2-Q4 yes -> Standard; else Lite.
 * Q5 and Q6 add follow-ups. The rules also render as text for crawlers.
 */
export default function TierCheck({ copy }: { copy: TierCheckCopy }) {
  const [answers, setAnswers] = useState<Answer[]>(() => copy.questions.map(() => "n"));
  const tierOf = (a: Answer[]) => (a[0] === "y" ? "regulated" : a.slice(1, 4).includes("y") ? "standard" : "lite");
  const set = (i: number, v: Answer) => {
    const next = answers.map((x, j) => (j === i ? v : x));
    setAnswers(next);
    try {
      // question = 1-based number, never the (translated) text.
      trackEvent("agk_tier_check_answer", { category: "engagement", question: i + 1, answer: v === "y" ? "yes" : "no", tier: tierOf(next), location: "tier-check" });
    } catch {
      // ignore
    }
  };
  const tier = tierOf(answers);
  const extras = [answers[4] === "y" ? copy.extraConnectors : null, answers[5] === "y" ? copy.extraPolicy : null].filter(Boolean) as string[];

  return (
    <div className="mt-10 grid gap-6 rounded-2xl border border-border bg-card p-5 sm:p-8 lg:grid-cols-[1.3fr_1fr]">
      <div>
        <h3 className="font-display text-xl font-semibold text-neutral-emphasis">{copy.heading}</h3>
        <p className="mt-2 text-sm text-neutral-muted">{copy.intro}</p>
        <ol className="mt-5 space-y-4">
          {copy.questions.map((q, i) => (
            <li key={q}>
              <fieldset>
                <legend className="text-[15px] leading-relaxed text-neutral-emphasis">
                  <span className="mr-2 font-mono text-xs text-primary">{i + 1}</span>
                  {q}
                </legend>
                <div className="mt-2 inline-flex rounded-lg border border-border p-0.5" role="radiogroup">
                  {(["y", "n"] as const).map((v) => (
                    <label
                      key={v}
                      className={cn(
                        "cursor-pointer rounded-md px-4 py-1.5 text-sm font-semibold transition-colors focus-within:ring-2 focus-within:ring-primary",
                        answers[i] === v ? "bg-primary text-primary-foreground" : "text-neutral-muted hover:text-neutral-emphasis",
                      )}
                    >
                      <input
                        type="radio"
                        name={`tier-q${i}`}
                        value={v}
                        checked={answers[i] === v}
                        onChange={() => set(i, v)}
                        className="sr-only"
                      />
                      {v === "y" ? copy.yes : copy.no}
                    </label>
                  ))}
                </div>
              </fieldset>
            </li>
          ))}
        </ol>
      </div>
      <div className="lg:sticky lg:top-36 lg:self-start">
        <div className="rounded-xl bg-primary/5 p-5 ring-1 ring-primary/20" aria-live="polite">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-primary">{copy.resultLabel}</p>
          <p className="mt-2 font-display text-3xl font-bold text-neutral-emphasis">{copy.tiers[tier]}</p>
          <p className="mt-2 text-sm text-neutral-muted">{copy.why[tier]}</p>
          <p className="mt-4 text-sm font-semibold text-neutral-emphasis">{copy.start[tier]}</p>
          {extras.length > 0 && (
            <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-neutral-muted">
              {extras.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          )}
          <a
            href="#lite-pack"
            data-agk-cta="tier_result_lite_pack"
            data-agk-tier={tier}
            className="mt-5 inline-flex min-h-11 items-center rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary-emphasis"
          >
            {copy.cta}
          </a>
        </div>
        <details className="mt-4 text-sm text-neutral-muted">
          <summary className="cursor-pointer font-semibold text-neutral-emphasis">{copy.rulesLabel}</summary>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            {copy.rules.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
          <p className="mt-2 text-xs">{copy.note}</p>
        </details>
      </div>
    </div>
  );
}
